import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Flame, Trophy, LogOut, Edit3, Save, X, Sparkles, Lock, Crown, Instagram, Youtube, MapPin, Camera, Eye, EyeOff, Share2, DoorOpen } from "lucide-react";
import Friends from "@/components/Friends";
import FriendsFeed from "@/components/FriendsFeed";
import FriendsLeaderboard from "@/components/FriendsLeaderboard";
import PrideBadges from "@/components/PrideBadges";
import TokenBalance from "@/components/TokenBalance";
import PacificClock from "@/components/PacificClock";
import OwnerAuditPanel from "@/components/OwnerAuditPanel";
import SpinWheel from "@/components/SpinWheel";
import PhoenixHatch from "@/components/PhoenixHatch";
import DailyChallenge from "@/components/DailyChallenge";
import StickerCollection from "@/components/StickerCollection";
import PinnedPlans from "@/components/PinnedPlans";
import WeeklyRecapSettings from "@/components/WeeklyRecapSettings";
import PublicProfileSettings from "@/components/PublicProfileSettings";
import DisplayPreferences from "@/components/DisplayPreferences";
import ManagePasskeys from "@/components/ManagePasskeys";
import HighlightReel from "@/components/HighlightReel";
import ProfileNotificationsFeed from "@/components/ProfileNotificationsFeed";
import { SOCIAL_PLATFORMS, SocialLinkRow } from "@/components/SocialLinks";
import ColorBackgroundPicker from "@/components/ColorBackgroundPicker";

// Mirror of backend PROFILE_COLOR_PALETTES — applied to the Profile page
// wrapper when user.profile_bg_color is set.
const PROFILE_BG_GRADIENTS = {
  red:    { from: "#1A0606", to: "#3A0F0F" },
  orange: { from: "#1F0F03", to: "#3A1A06" },
  yellow: { from: "#1F1A03", to: "#3A2F06" },
  green:  { from: "#031A0F", to: "#062F1A" },
  blue:   { from: "#06121F", to: "#0F1F3A" },
  purple: { from: "#160B22", to: "#2F0F3A" },
};
import ShareProfileModal from "@/components/ShareProfileModal";
import PremiumStatus from "@/components/PremiumStatus";
import OmniDollarsPanel from "@/components/OmniDollarsPanel";
import MyOrders from "@/components/MyOrders";
import ShopAdmin from "@/components/ShopAdmin";
import SafeSection from "@/components/SafeSection";
import CurrencyVouchersPanel from "@/components/CurrencyVouchersPanel";
import OwnerVoucherGrantPanel from "@/components/OwnerVoucherGrantPanel";
import BuildStatusBanner from "@/components/BuildStatusBanner";
import VialRack from "@/components/VialRack";
import AmbergrisPanel from "@/components/AmbergrisPanel";
import PyrotechnicSetBadge from "@/components/PyrotechnicSetBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIVACY_FIELDS = [
  { key: "full_name", label: "Full Name" },
  { key: "location", label: "Location" },
  { key: "email", label: "Email Address" },
  { key: "bio", label: "Bio" },
  { key: "streaks", label: "Streaks (sign-in & activity)" },
  { key: "badges", label: "Badge count" },
  { key: "activity_counts", label: "Activity counts (workouts/meals/AI plans)" },
  { key: "social_instagram", label: "Instagram handle" },
  { key: "social_tiktok", label: "TikTok handle" },
  { key: "social_youtube", label: "YouTube handle" },
  { key: "social_facebook", label: "Facebook" },
  { key: "social_threads", label: "Threads" },
  { key: "social_x", label: "X / Twitter" },
  { key: "social_discord", label: "Discord" },
];

const DEFAULT_PRIVACY = {
  full_name: true, location: false, email: false, bio: false,
  streaks: true, badges: true, activity_counts: true,
  social_instagram: false, social_tiktok: false, social_youtube: false,
  social_facebook: false, social_threads: false, social_x: false, social_discord: false,
};

// Resize a File to a square JPEG data URL ≤ ~700KB
async function fileToResizedDataUrl(file, maxDim = 600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        const out = Math.min(maxDim, size);
        const canvas = document.createElement("canvas");
        canvas.width = out;
        canvas.height = out;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, size, size, 0, 0, out, out);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  return (
    <SafeSection label="Profile Page">
      <ProfileInner />
    </SafeSection>
  );
}

function ProfileInner() {
  const { user, logout, updateProfile, togglePremium, refreshUser } = useAuth();
  const [allBadges, setAllBadges] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [privacy, setPrivacy] = useState(DEFAULT_PRIVACY);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [leavingParties, setLeavingParties] = useState(false);
  const fileInputRef = useRef(null);

  // 🚪 Profile-level "Leave All Parties" — confirms then nukes membership
  // + pending invites across every active party the user is in.
  const onLeaveAllParties = async () => {
    if (leavingParties) return;
    const confirmed = window.confirm(
      "Leave all parties?\n\n" +
      "You'll be removed from every active party you're in, " +
      "and every pending invite will be declined. " +
      "This cannot be undone."
    );
    if (!confirmed) return;
    setLeavingParties(true);
    try {
      const r = await axios.post(`${API}/party/leave-all`);
      const d = r.data || {};
      const parts = [];
      if (d.parties_left?.length) parts.push(`left ${d.parties_left.length} party/parties`);
      if (d.parties_dissolved?.length) parts.push(`dissolved ${d.parties_dissolved.length}`);
      if (d.leadership_transfers?.length) parts.push(`transferred ${d.leadership_transfers.length} leadership`);
      if (d.invites_declined) parts.push(`declined ${d.invites_declined} invite(s)`);
      const summary = parts.length ? parts.join(" · ") : "Nothing to leave — you were already free.";
      toast.success(`🚪 ${summary}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave parties.");
    } finally {
      setLeavingParties(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/badges/all`).then((r) => setAllBadges(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        sex: user.profile?.sex || "",
        age: user.profile?.age || "",
        height: user.profile?.height || "",
        weight: user.profile?.weight || "",
        goal: user.profile?.goal || "",
        bio: user.profile?.bio || "",
        full_name: user.profile?.full_name || "",
        location: user.profile?.location || "",
        social_instagram: user.profile?.social_instagram || "",
        social_tiktok: user.profile?.social_tiktok || "",
        social_youtube: user.profile?.social_youtube || "",
        social_facebook: user.profile?.social_facebook || "",
        social_threads: user.profile?.social_threads || "",
        social_x: user.profile?.social_x || "",
        social_discord: user.profile?.social_discord || "",
      });
      setPrivacy({ ...DEFAULT_PRIVACY, ...(user.privacy || {}) });
    }
  }, [user]);

  if (!user || user === false) {
    return (
      <section className="py-32 text-center" data-testid="profile-section-empty">
        <p className="text-neutral-400">Please sign in to view your profile.</p>
      </section>
    );
  }

  const earned = new Set(user.badges || []);
  const signin = user.signin_streak || {};
  const activity = user.activity_streak || {};
  const counts = user.activity_counts || {};

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 600);
      await updateProfile({ picture: dataUrl });
      toast.success("Profile photo updated.");
    } catch {
      toast.error("Could not upload photo. Try a smaller image.");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const togglePrivacyField = async (key) => {
    const next = { ...privacy, [key]: !privacy[key] };
    setPrivacy(next);
    try {
      await updateProfile({ privacy: { [key]: next[key] } });
    } catch {
      // revert on failure
      setPrivacy(privacy);
      toast.error("Could not save privacy setting.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name: form.name,
        sex: form.sex,
        age: form.age ? parseInt(form.age, 10) || null : null,
        height: form.height,
        weight: form.weight,
        goal: form.goal,
        bio: form.bio,
        full_name: form.full_name,
        location: form.location,
        social_instagram: form.social_instagram,
        social_tiktok: form.social_tiktok,
        social_youtube: form.social_youtube,
        social_facebook: form.social_facebook,
        social_threads: form.social_threads,
        social_x: form.social_x,
        social_discord: form.social_discord,
      });
      setEditing(false);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="profile"
      data-testid="profile-section"
      className="py-24 md:py-32 min-h-screen pt-32"
      style={{
        background: PROFILE_BG_GRADIENTS[user.profile_bg_color]
          ? `linear-gradient(to bottom, ${PROFILE_BG_GRADIENTS[user.profile_bg_color].from}, ${PROFILE_BG_GRADIENTS[user.profile_bg_color].to}, #0A0A0A)`
          : "linear-gradient(to bottom, #0A0A0A, #13081f, #0A0A0A)",
      }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 🚀 Owner-only build status: chip in preview, stale-warning on production */}
        <SafeSection label="Build Status">
          <BuildStatusBanner />
        </SafeSection>
        <div className="flex justify-end mb-3">
          <PacificClock size="sm" testId="profile-clock" />
        </div>
        {/* Header */}
        <div className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-6 md:p-8 mb-6 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-shrink-0 relative group">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                data-testid="profile-photo"
                className="w-24 h-24 object-contain bg-black border-2 border-[#D4AF37]"
              />
            ) : (
              <div
                data-testid="profile-photo-placeholder"
                className="w-24 h-24 bg-[#4C1D95] border-2 border-[#D4AF37] flex items-center justify-center font-display text-4xl text-[#D4AF37]"
              >
                {(user.name || "A").charAt(0).toUpperCase()}
              </div>
            )}
            <button
              data-testid="profile-photo-upload"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-2 -right-2 w-9 h-9 bg-[#D4AF37] hover:bg-[#f5d96b] text-black flex items-center justify-center border-2 border-black disabled:opacity-50 transition-colors"
              title="Upload profile photo"
            >
              <Camera size={14} strokeWidth={2.5} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPhotoPick}
              className="hidden"
              data-testid="profile-photo-input"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">
                {user.name}
              </h1>
              {user.role === "owner" && (
                <span
                  data-testid="owner-badge"
                  className="bg-gradient-to-r from-[#D4AF37] to-[#f5d96b] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1"
                  title="Site Owner — full access"
                >
                  <Crown size={11} strokeWidth={2.5} /> Owner
                </span>
              )}
              <PyrotechnicSetBadge />
              {user.is_premium && user.role !== "owner" && (
                <span className="bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1">
                  <Sparkles size={10} /> Premium
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-400">{user.email}</p>
            {user.profile?.full_name && (
              <p className="text-sm text-[#D4AF37] mt-1 font-bold">
                {user.profile.full_name}
              </p>
            )}
            {user.profile?.location && (
              <p className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1">
                <MapPin size={12} /> {user.profile.location}
              </p>
            )}
            <SocialLinkRow source={user.profile} testIdPrefix="profile" className="mt-2" />
            {user.profile?.bio && (
              <p className="text-sm text-neutral-300 mt-2 italic">"{user.profile.bio}"</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              data-testid="profile-edit"
              onClick={() => setEditing((s) => !s)}
              className="btn-omni btn-outline-gold text-xs"
            >
              {editing ? <><X size={14} /> Cancel</> : <><Edit3 size={14} /> Edit</>}
            </button>
            {user.role !== "owner" && (
              <button
                data-testid="profile-premium-toggle"
                onClick={togglePremium}
                className="btn-omni btn-purple text-xs"
                title="Demo unlock"
              >
                {user.is_premium ? "Disable Premium" : <><Sparkles size={12} /> Try Premium</>}
              </button>
            )}
            <button
              data-testid="profile-leave-all-parties"
              onClick={onLeaveAllParties}
              disabled={leavingParties}
              className="btn-omni btn-outline-gold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove yourself from every active party + decline all pending invites"
            >
              <DoorOpen size={14} /> {leavingParties ? "Leaving…" : "Leave All Parties"}
            </button>
            <button
              data-testid="profile-logout"
              onClick={logout}
              className="btn-omni btn-red text-xs"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Edit form */}
        {editing && (
          <div className="bg-[#0F0F0F] border border-zinc-800 p-6 mb-6 grid sm:grid-cols-2 gap-4">
            <Field label="Name">
              <input className="input-omni" value={form.name} onChange={upd("name")} />
            </Field>
            <Field label="Sex">
              <select className="input-omni" value={form.sex} onChange={upd("sex")}>
                <option value="">—</option><option value="male">Male</option>
                <option value="female">Female</option><option value="other">Other</option>
              </select>
            </Field>
            <Field label="Age">
              <input className="input-omni" value={form.age} onChange={upd("age")} placeholder="32" />
            </Field>
            <Field label="Height">
              <input className="input-omni" value={form.height} onChange={upd("height")} placeholder="5'10&quot; or 178cm" />
            </Field>
            <Field label="Weight">
              <input className="input-omni" value={form.weight} onChange={upd("weight")} placeholder="185 lb or 84 kg" />
            </Field>
            <Field label="Primary Goal">
              <select className="input-omni" value={form.goal} onChange={upd("goal")}>
                <option value="">—</option>
                <option value="build_muscle">Build Muscle</option>
                <option value="fat_loss">Fat Loss</option>
                <option value="strength">Strength</option>
                <option value="athletic_performance">Athletic Performance</option>
                <option value="stage_prep">Stage Prep</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Bio (one line)">
                <input className="input-omni" value={form.bio} onChange={upd("bio")}
                  placeholder='"Flying higher, one rep at a time."' />
              </Field>
            </div>
            <div className="sm:col-span-2 border-t border-zinc-800 pt-4 -mt-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
                Identity & Socials
              </div>
            </div>
            <Field label="Full Name (real name)">
              <input
                data-testid="profile-input-fullname"
                className="input-omni" value={form.full_name} onChange={upd("full_name")}
                placeholder='Nebiyou Howard "Roc" May'
              />
            </Field>
            <Field label="Location">
              <input
                data-testid="profile-input-location"
                className="input-omni" value={form.location} onChange={upd("location")}
                placeholder="USA"
              />
            </Field>
            {SOCIAL_PLATFORMS.map((p) => (
              <Field key={p.key} label={p.label}>
                <input
                  data-testid={`profile-input-${p.key.replace("social_", "")}`}
                  className="input-omni"
                  value={form[p.key] || ""}
                  onChange={upd(p.key)}
                  placeholder={p.placeholder}
                />
              </Field>
            ))}
            <button
              data-testid="profile-save"
              onClick={save}
              disabled={saving}
              className="btn-omni btn-red sm:col-span-2 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        )}

        {/* Privacy Controls */}
        <div
          data-testid="privacy-section"
          className="bg-[#0F0F0F] border border-zinc-800 p-6 mb-6"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 inline-flex items-center gap-1.5">
                <Lock size={11} /> Privacy
              </div>
              <h2 className="font-display text-2xl text-white">Who sees what?</h2>
              <p className="text-xs text-neutral-400 mt-1 max-w-md">
                Friends always see your full profile. Strangers and pending
                requests only see what you toggle on below.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {PRIVACY_FIELDS.map((f) => {
              const on = !!privacy[f.key];
              return (
                <button
                  key={f.key}
                  data-testid={`privacy-toggle-${f.key}`}
                  onClick={() => togglePrivacyField(f.key)}
                  className={`flex items-center justify-between gap-3 px-3 py-2.5 border-2 transition-colors text-left ${
                    on
                      ? "border-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-zinc-800 bg-[#0A0A0A] hover:border-zinc-700"
                  }`}
                >
                  <span className="text-sm text-white truncate">{f.label}</span>
                  <span
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
                      on ? "text-[#D4AF37]" : "text-neutral-500"
                    }`}
                  >
                    {on ? (
                      <>
                        <Eye size={11} /> Public
                      </>
                    ) : (
                      <>
                        <EyeOff size={11} /> Private
                      </>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Streak cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <StreakCard
            testId="streak-signin"
            title="Sign-In Streak"
            current={signin.current || 0}
            longest={signin.longest || 0}
            color="#D4AF37"
          />
          <StreakCard
            testId="streak-activity"
            title="Activity Streak"
            current={activity.current || 0}
            longest={activity.longest || 0}
            color="#DC2626"
          />
        </div>

        {/* Activity counts */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { l: "AI Plans", v: counts.ai_plan || 0 },
            { l: "Workouts", v: counts.workout || 0 },
            { l: "Meals", v: counts.food || 0 },
          ].map((s) => (
            <div key={s.l} className="bg-[#0F0F0F] border border-zinc-800 p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                {s.l}
              </div>
              <div className="font-display text-3xl text-white">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Badges */}
        <div className="bg-[#0F0F0F] border border-zinc-800 p-6">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                Achievements
              </div>
              <h2 className="font-display text-3xl text-white">
                Badges <span className="text-[#D4AF37]">({earned.size}/{allBadges.length})</span>
              </h2>
            </div>
            <Trophy className="text-[#D4AF37]" />
          </div>
          <BadgeGrid badges={allBadges} earned={earned} user={user} />
        </div>

        {/* Phoenix Tokens */}
        <SafeSection label="Phoenix Tokens"><TokenBalance variant="full" /></SafeSection>

        {/* 🧪 Vial Rack — Nebula Vials inventory + active buffs */}
        <SafeSection label="Vial Rack"><VialRack /></SafeSection>

        {/* 🥇 Ambergris — banked BG carryover + chest combine + open */}
        <SafeSection label="Ambergris"><AmbergrisPanel /></SafeSection>

        {/* Premium subscription status */}
        <SafeSection label="Premium Status"><PremiumStatus /></SafeSection>

        {/* Omni Dollars wallet */}
        <SafeSection label="Omni Dollars"><OmniDollarsPanel /></SafeSection>

        {/* 💰🎁🕰️ Deanah & Muki's Unsanctioned Currencies — canon Feb 2026 */}
        <SafeSection label="Unsanctioned Currencies"><CurrencyVouchersPanel /></SafeSection>

        {/* My shop orders */}
        <SafeSection label="My Orders"><MyOrders /></SafeSection>

        {/* Daily Challenge */}
        <SafeSection label="Daily Challenge"><DailyChallenge /></SafeSection>

        {/* Pinned AI Coach plan + Workout */}
        <SafeSection label="Pinned Plans">
          <section data-testid="pinned-section" className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              My Pinned Plans
            </div>
            <h3 className="font-display text-3xl text-white mb-4">📌 What I'm Following</h3>
            <PinnedPlans />
          </section>
        </SafeSection>

        {/* Email preferences (Weekly Recap opt-out) */}
        <SafeSection label="Weekly Recap Settings"><WeeklyRecapSettings /></SafeSection>

        {/* 🔐 Face ID & Passkey management */}
        <SafeSection label="Face ID & Passkeys"><ManagePasskeys /></SafeSection>

        {/* Display preferences (Phoenix Flame cursor toggle, etc.) */}
        <SafeSection label="Display Preferences"><DisplayPreferences /></SafeSection>

        {/* 🏆 Your Highlight Reel */}
        <SafeSection label="Highlight Reel">
          <section className="mt-10">
            <h3 className="font-display text-2xl text-white mb-3">🏆 Your Highlight Reel</h3>
            <HighlightReel self />
          </section>
        </SafeSection>

        {/* 🔔 Feb 2026 — Notifications live on the profile too. Auto-
            marks everything checked off after 1.5s of visibility so
            the player sees the fresh dots before they clear. */}
        <ProfileNotificationsFeed />

        {/* ⚡ Aether & Ash — character power rating + full stat sheet.
            Owner directive Feb 2026: "A page showing current status
            and stats... accumulative number of how strong a character
            is." */}
        <section className="mt-10">
          <a
            href="/aether-and-ash"
            data-testid="profile-aether-and-ash-link"
            className="group block rounded-xl border border-amber-700/40 bg-gradient-to-br from-amber-950/40 via-black to-rose-950/30 p-4 hover:border-amber-500/60 hover:shadow-[0_0_40px_-15px_rgba(251,191,36,0.6)] transition-all"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400/70">Open Your Sheet</div>
                <div className="font-serif text-2xl text-amber-100 mt-0.5">⚡ Aether &amp; Ash</div>
                <div className="text-xs text-amber-300/70 mt-1">
                  Full stats · total power rating · tier climb — updates every time you add gear, mounts, shards, or a new pet.
                </div>
              </div>
              <div className="text-3xl text-amber-300 group-hover:text-amber-100 transition-colors">→</div>
            </div>
          </a>
        </section>

        {/* 🎓 Re-take onboarding tour */}
        <section className="mt-10">
          <h3 className="font-display text-2xl text-white mb-2">🎓 Help & Tour</h3>
          <p className="text-sm text-neutral-400 mb-3">
            New to OmniMuscle or want a quick refresher? Re-play the 60-second walkthrough.
          </p>
          <button
            data-testid="profile-retake-tour"
            onClick={() => {
              try { localStorage.removeItem("omni:tour-done:v1"); } catch { /* noop */ }
              window.dispatchEvent(new Event("omnimuscle:start-tour"));
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="bg-[#0F0F0F] hover:bg-zinc-900 border border-[#D4AF37] text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-bold py-2.5 px-5 transition-colors"
          >
            ▶ Re-take Tour
          </button>
        </section>

        {/* Phoenix Feather color backgrounds 🪶 */}
        <SafeSection label="Color Background"><ColorBackgroundPicker /></SafeSection>

        {/* Public profile sharing — handle + visibility + share modal */}
        <SafeSection label="Public Profile Settings"><PublicProfileSettings /></SafeSection>

        {/* Spin Wheel */}
        <SafeSection label="Spin Wheel"><SpinWheel /></SafeSection>

        {/* Phoenix Hatch 🐦‍🔥 (Premium daily reveal) */}
        <SafeSection label="Phoenix Hatch"><PhoenixHatch /></SafeSection>

        {/* Sticker Collection 🎨 */}
        <SafeSection label="Sticker Collection"><StickerCollection /></SafeSection>

        {/* Owner-only: Anti-cheat reset */}
        {user.role === "owner" && (
          <SafeSection label="Owner Reset Panel">
            <OwnerResetPanel onDone={() => refreshUser && refreshUser()} />
          </SafeSection>
        )}

        {/* Owner-only: Token audit */}
        {user.role === "owner" && (
          <SafeSection label="Owner Audit Panel"><OwnerAuditPanel /></SafeSection>
        )}

        {/* Owner-only: Shop admin */}
        {user.role === "owner" && (
          <SafeSection label="Shop Admin"><ShopAdmin /></SafeSection>
        )}

        {/* 🛠️💰 Owner-only: Grant Deanah & Muki's currency vouchers */}
        {user.role === "owner" && (
          <SafeSection label="Voucher Grant Panel">
            <OwnerVoucherGrantPanel selfUserId={user.user_id} />
          </SafeSection>
        )}

        {/* Pride / Identity badges (opt-in) */}
        <SafeSection label="Pride Badges">
          <PrideBadges
            claimed={user.pride_claims || []}
            onChange={() => refreshUser && refreshUser()}
          />
        </SafeSection>

        {/* Friends */}
        <SafeSection label="Friends"><Friends /></SafeSection>

        {/* Friends 2.0 — Activity Feed */}
        <SafeSection label="Friends Feed"><FriendsFeed /></SafeSection>

        {/* Friends 2.0 — Leaderboard */}
        <SafeSection label="Friends Leaderboard"><FriendsLeaderboard /></SafeSection>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function StreakCard({ testId, title, current, longest, color }) {
  return (
    <div data-testid={testId} className="bg-[#0F0F0F] border border-zinc-800 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Flame size={20} style={{ color }} />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color }}>
          {title}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-display text-6xl text-white leading-none">{current}</div>
        <div className="text-sm text-neutral-500">days</div>
      </div>
      <div className="text-xs text-neutral-500 mt-2">
        Longest: <span className="text-white font-bold">{longest}</span> days
      </div>
    </div>
  );
}


function OwnerResetPanel({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function doReset() {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reset-tokens-and-streaks`, {}, { withCredentials: true });
      toast.success(
        `Reset complete — ${data.users_updated} users wiped, ${data.token_events_deleted} ledger entries cleared.`,
        { duration: 6000 }
      );
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div
      data-testid="owner-reset-panel"
      className="bg-[#0F0F0F] border border-red-900/60 p-6 mt-6"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-1">
        Owner Tools · Anti-Cheat
      </div>
      <h2 className="font-display text-2xl text-white mb-2">
        Reset <span className="text-red-500">Phoenix Tokens</span> & Streaks
      </h2>
      <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
        Wipes <strong className="text-white">every</strong> user's token balance + sign-in / activity / food streaks
        (including yours, per your fairness rule). Badges, friends, profile, and activity counts are kept.
        Use this after detecting spam to restore a fair leaderboard.
      </p>
      {!confirming ? (
        <button
          data-testid="owner-reset-trigger"
          disabled={busy}
          onClick={() => setConfirming(true)}
          className="bg-red-700 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors disabled:opacity-50"
        >
          Reset Tokens & Streaks
        </button>
      ) : (
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-sm text-red-400 font-bold">Are you sure? This can't be undone.</span>
          <button
            data-testid="owner-reset-confirm"
            disabled={busy}
            onClick={doReset}
            className="bg-red-700 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 disabled:opacity-50"
          >
            {busy ? "Resetting..." : "Yes — wipe everything"}
          </button>
          <button
            data-testid="owner-reset-cancel"
            disabled={busy}
            onClick={() => setConfirming(false)}
            className="border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs px-5 py-3 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}


// ===== Badge categorization =====
// Maps every badge type/criteria to a UI tab.
const BADGE_CATEGORIES = [
  { key: "all",       label: "All",       match: () => true },
  { key: "mastery",   label: "Mastery",   match: (b) => b.type === "collection_pct" },
  { key: "streaks",   label: "Streaks",   match: (b) => b.type === "streak" || b.type === "signin_streak" || b.type === "food_streak" || b.type === "sleep_streak" || b.type === "water_streak" || b.type === "cardio_streak" },
  { key: "vitals",    label: "Vitals",    match: (b) => b.type === "sleep_streak" || b.type === "water_streak" || b.type === "quick_count" },
  { key: "quests",    label: "Quests",    match: (b) => b.type === "challenge_streak" },
  { key: "spins",     label: "Spins",     match: (b) => b.type === "spin_streak" },
  { key: "activity",  label: "Activity",  match: (b) => b.type === "activity_count" || (b.type === "identity" && ["5_ai_plans","20_workouts","30_day_active_streak","trifecta_day"].includes(b.criteria)) },
  { key: "social",    label: "Social",    match: (b) => b.type === "friends_count" || b.type === "share_count" || b.type === "dm_count" || (b.type === "identity" && ["first_friend"].includes(b.criteria)) },
  { key: "identity",  label: "Identity",  match: (b) => (b.type === "identity" && ["joined_first_month","comeback_14"].includes(b.criteria)) || b.type === "premium" },
  { key: "pride",     label: "Pride",     match: (b) => b.type === "pride" },
];

function badgeProgress(b, user, allBadges, earned) {
  if (!user) return null;
  const counts = user.activity_counts || {};
  // PT date — backend daily_done is keyed by PT (00:00 PT reset), so frontend
  // lookups must also be PT or "today" disappears at 5pm PT.
  const _ptParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const _y = _ptParts.find((p) => p.type === "year")?.value;
  const _m = _ptParts.find((p) => p.type === "month")?.value;
  const _d = _ptParts.find((p) => p.type === "day")?.value;
  const todayIso = `${_y}-${_m}-${_d}`;
  const dailyDone = (user.daily_done || {})[todayIso] || {};
  const sig = (s, k = "current") => (user[s] || {})[k] || 0;
  let current = 0, target = 1;
  if (b.type === "streak") { current = sig("activity_streak"); target = b.threshold; }
  else if (b.type === "signin_streak") { current = sig("signin_streak"); target = b.threshold; }
  else if (b.type === "food_streak") { current = sig("food_streak"); target = b.threshold; }
  else if (b.type === "spin_streak") { current = sig("spin_streak"); target = b.threshold; }
  else if (b.type === "challenge_streak") { current = sig("challenge_streak"); target = b.threshold; }
  else if (b.type === "activity_count") { current = counts[b.activity] || 0; target = b.threshold; }
  else if (b.type === "friends_count") { current = (user.friends || []).length; target = b.threshold; }
  else if (b.type === "share_count") { current = (user.share_counts || {})[b.kind || "workout"] || 0; target = b.threshold; }
  else if (b.type === "dm_count") { current = user.dm_sent_count || 0; target = b.threshold; }
  else if (b.type === "sleep_streak") { current = sig("sleep_streak"); target = b.threshold; }
  else if (b.type === "water_streak") { current = sig("water_streak"); target = b.threshold; }
  else if (b.type === "quick_count") { current = user.quick_log_count || 0; target = b.threshold; }
  else if (b.type === "identity") {
    const c = b.criteria;
    if (c === "5_ai_plans")    { current = counts.ai_plan || 0; target = 5; }
    else if (c === "20_workouts") { current = counts.workout || 0; target = 20; }
    else if (c === "30_day_active_streak") { current = sig("activity_streak"); target = 30; }
    else if (c === "trifecta_day") {
      current = ["workout","food","ai_plan"].filter((k) => dailyDone[k]).length; target = 3;
    }
    else if (c === "first_friend") { current = (user.friends || []).length > 0 ? 1 : 0; target = 1; }
    else { return null; }
  }
  else if (b.type === "collection_pct") {
    if (!allBadges || !earned) return null;
    const nonCol = allBadges.filter((x) => x.type !== "collection_pct");
    const nonColEarned = nonCol.filter((x) => earned.has(x.id)).length;
    current = Math.round((nonColEarned / Math.max(1, nonCol.length)) * 100);
    target = b.threshold;
  }
  else { return null; }
  const pct = Math.min(100, Math.round((current / Math.max(1, target)) * 100));
  return { current, target, pct, completed: current >= target };
}

function BadgeGrid({ badges, earned, user }) {
  const [active, setActive] = useState("all");
  const [shareBadge, setShareBadge] = useState(null);
  const filtered = badges.filter(BADGE_CATEGORIES.find((c) => c.key === active).match);

  // Overall completion
  const overallPct = Math.round((earned.size / Math.max(1, badges.length)) * 100);

  // Find the "next badge" — locked badge closest to unlocking (highest pct, prefer non-zero)
  const next = badges
    .filter((b) => !earned.has(b.id))
    .map((b) => ({ badge: b, prog: badgeProgress(b, user, badges, earned) }))
    .filter((x) => x.prog && x.prog.target > 0 && !x.prog.completed)
    .sort((a, b) => (b.prog.pct - a.prog.pct) || (a.prog.target - a.prog.current) - (b.prog.target - b.prog.current))[0];

  return (
    <div data-testid="badge-grid">
      {/* Overall completion bar */}
      <div className="mb-5">
        <div className="flex justify-between text-[10px] uppercase tracking-widest font-bold mb-2">
          <span className="text-neutral-500">Collection</span>
          <span data-testid="badge-completion" className="text-[#D4AF37]">
            {earned.size} of {badges.length} · {overallPct}%
          </span>
        </div>
        <div className="h-2 bg-[#0A0A0A] border border-zinc-900 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#4C1D95] via-[#DC2626] to-[#D4AF37] transition-all duration-700"
            style={{ width: `${overallPct}%` }}
          />
        </div>
      </div>

      {/* Next Badge preview */}
      {next && (
        <div
          data-testid="next-badge"
          className="bg-[#0A0A0A] border border-[#D4AF37]/40 p-4 mb-5"
        >
          <div className="flex items-center gap-4">
            <div className="text-4xl opacity-60 grayscale">{next.badge.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-0.5">
                Next Badge
              </div>
              <div className="font-display text-lg text-white truncate">
                {next.badge.title}
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                {next.prog.current} / {next.prog.target} ·
                <span className="text-[#D4AF37] font-bold ml-1">
                  {next.prog.target - next.prog.current} to go
                </span>
              </div>
              <div className="h-1.5 bg-[#0F0F0F] border border-zinc-900 mt-2 overflow-hidden">
                <div
                  className="h-full bg-[#D4AF37] transition-all duration-500"
                  style={{ width: `${next.prog.pct}%` }}
                  data-testid="next-badge-bar"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-zinc-900 pb-3">
        {BADGE_CATEGORIES.map((cat) => {
          const catBadges = badges.filter(cat.match);
          const catEarned = catBadges.filter((b) => earned.has(b.id)).length;
          const isActive = active === cat.key;
          return (
            <button
              key={cat.key}
              data-testid={`badge-tab-${cat.key}`}
              onClick={() => setActive(cat.key)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
                isActive
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-zinc-800 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              }`}
            >
              {cat.label}
              <span className={`ml-1.5 ${isActive ? "text-[#D4AF37]" : "text-neutral-600"}`}>
                {catEarned}/{catBadges.length}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-6">
          No badges in this category yet.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((b) => {
            const got = earned.has(b.id);
            const prog = !got ? badgeProgress(b, user, badges, earned) : null;
            return (
              <div
                key={b.id}
                data-testid={`badge-${b.id}`}
                className={`p-4 border-2 text-center transition-all relative group ${
                  got ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-zinc-800 bg-[#0A0A0A] opacity-70"
                }`}
              >
                <div className="text-3xl mb-1">
                  {got ? b.icon : <Lock size={24} className="inline text-zinc-600" />}
                </div>
                <div className="font-display text-sm text-white leading-tight">{b.title}</div>
                <div className="text-[10px] text-neutral-500 mt-1 uppercase tracking-widest">
                  {b.type === "activity_count"
                    ? `${b.threshold}×`
                    : b.threshold
                    ? `${b.threshold}-day`
                    : b.type.replace(/_/g, " ")}
                </div>
                {!got && prog && (
                  <div className="mt-2">
                    <div className="h-1 bg-[#0F0F0F] border border-zinc-900 overflow-hidden">
                      <div
                        className="h-full bg-[#D4AF37] transition-all"
                        style={{ width: `${prog.pct}%` }}
                      />
                    </div>
                    <div className="text-[9px] text-neutral-600 mt-1 tabular-nums">
                      {prog.current}/{prog.target}
                    </div>
                  </div>
                )}
                {got && user?.public_profile_enabled && user?.handle && (
                  <button
                    data-testid={`badge-share-${b.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareBadge(b);
                    }}
                    className="absolute top-1.5 right-1.5 w-7 h-7 bg-[#0A0A0A]/90 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Share this badge"
                  >
                    <Share2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {shareBadge && (
        <ShareProfileModal
          handle={user?.handle}
          name={user?.name}
          badge={{ id: shareBadge.id, title: shareBadge.title, icon: shareBadge.icon }}
          onClose={() => setShareBadge(null)}
        />
      )}
    </div>
  );
}
